import React, { useContext, useEffect, useState } from 'react';
import MUIDataTable, { DisplayData, MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { Box, Button, Chip, IconButton, MenuItem, Select, Tooltip } from '@mui/material';
import {
  getContacts,
  getFirmUsers,
  getOrganisations,
  removeXeroConnection,
  syncTenants,
  tagOrganisation,
  untagOrganisation,
  updateOrganisationContact,
  updateOrganisationManager,
  updateOrganisations,
} from '../rest/BackendApi';
import { ThemeProvider } from '@mui/material/styles';
import AppConfig from '../AppConfig';
import SyncIcon from '@mui/icons-material/Sync';
import BatchIcon from '@mui/icons-material/DynamicFeed';
import EditIcon from '@mui/icons-material/Edit';
import {
  booleanFilter,
  customFilterWithPrefix,
  getColumnFilter,
  getMuiTheme,
  NoMatches,
  useTableBodyHeight,
  withReadableFilters,
} from '../components/Datatable';
import AddIcon from '@mui/icons-material/Add';
import Loading from '../components/Loading';
import { PeriodEnum } from '../model/PeriodEnum';
import { humanize, plural } from '../util/StringUtil';
import NoOrganisation from '../components/NoOrganisation';
import { SubText } from '../components/StyledComponent';
import { PageHeader } from '../components/Page';
import OrgSyncStatusDisplay from '../components/SyncStatus';
import dayjs from 'dayjs';
import { Organisation, OrganisationContact, Tag, XpmClient } from '../types/OrganisationType';
import UserContext from '../UserContext';
import ConfirmDialog from '../components/ConfirmDialog';
import DialogSelect, { KeyValue } from '../components/DialogSelect';
import SupportLink from '../components/SupportLink';
import XeroLink, { ExternalLink } from '../components/XeroLink';
import styles from '../style/common.module.css';
import { AddTextField } from '../components/AddTextField';
import AddTagIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { getDatatableColumnConfig, getDatatableConfig, getStoredColumnFilter } from '../util/LocalStorageUtil';
import { getFullContactName, getFullName } from '../util/DataUtil';
import { displayDate } from '../util/TimeUtil';
import MessageContext, { empty, error, progress, success } from '../MessageContext';
import { CheckBox } from '../components/CheckBox';
import { hasFeature } from '../business/Subscription';
import { DisplayWithSeeMoreChip } from '../components/DisplayWithSeeMoreChip';
import BatchUpdateOrganisationDialog, { BatchUpdateOrganisationDialogProps } from '../components/BatchUpdateOrganisationDialog';
import { NONE_LABEL } from '../util/Constants';
import { syncResultMessage } from '../business/SyncResult';

const EMPTY_CELL = '—';
const SETUP_FILTER_COLUMNS = ['manager', 'contact'] as const;
type SetupFilterColumn = (typeof SETUP_FILTER_COLUMNS)[number];
type SetupFilters = Record<SetupFilterColumn, string[]>;

/** Organisations missing the details reminders rely on. */
export function countMissingSetup(organisations: Organisation[]) {
  return {
    manager: organisations.filter(org => !org.manager).length,
    contact: organisations.filter(org => !org.contact).length,
  };
}

type DisconnectConfirmDataType = {
  open: boolean;
  message?: string;
  disconnectFunction?: Function;
};

type SelectDialogDataType = {
  open: boolean;
  selectFunction?: Function;
  loadData: () => Promise<KeyValue[]>;
  initialValue?: string;
  title?: string;
  emptyState?: string;
  subtitle?: string;
  label?: string;
  noneLabel?: string;
};

export default function OrganisationPage() {
  const [loading, setLoading] = useState(true);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [disconnectConfirmData, setDisconnectConfirmData] = useState<DisconnectConfirmDataType>({ open: false });
  const [selectDialogData, setSelectDialogData] = useState<SelectDialogDataType>({
    open: false,
    loadData: async () => {
      return [];
    },
  });
  const userContext = useContext(UserContext);
  const messageContext = useContext(MessageContext);

  const user = userContext.user;

  const [setupFilters, setSetupFilters] = useState<SetupFilters>(() => ({
    manager: getStoredColumnFilter('organisationConfig', 'manager'),
    contact: getStoredColumnFilter('organisationConfig', 'contact'),
  }));
  const [batchUpdateDialogData, setBatchUpdateDialogData] = useState<BatchUpdateOrganisationDialogProps>({
    open: false,
  });

  const shouldShowOpenAccess = hasFeature(user, 'MultiUser') && user?.firm?.openAccess;
  const shouldShowXpm = hasFeature(user, 'XPM');

  async function loadOrganisations() {
    setLoading(true);
    const organisations = await getOrganisations();
    if (organisations) {
      setOrganisations(organisations);
    }
    setLoading(false);
  }

  const onSyncReady = (syncStatus: any, attempt: number) => {
    if (syncStatus == null) {
      return;
    }
    // If attempt was greater than 1 or attempt 1 completed less than few seconds ago we refresh
    let syncCompletionTimeInMs = dayjs().diff(dayjs.utc(syncStatus.updateDate));
    if (attempt > 1 || syncCompletionTimeInMs < 5000) {
      loadOrganisations();
    }
  };

  async function disconnectOrganisation(orgId: number) {
    setLoading(true);
    let removed = await removeXeroConnection(orgId);
    if (!removed) {
      messageContext.setMessage(error('Failed to disconnect organisation from Xero. Log out and log back in if the problem persists.'));
      setLoading(false);
    } else {
      if (userContext.refreshUser) {
        userContext.refreshUser();
      }

      loadOrganisations();
    }
  }

  async function openBatchUpdatePopup(selected: Organisation[], setSelected: Function) {
    const orgIds = selected.map(row => row.id);
    if (orgIds.length == 0) {
      messageContext.setMessage(error('No organisation selected'));
      return;
    }

    setBatchUpdateDialogData({
      open: true,
      firmId: user?.firm?.id,
      orgIds,
      onClose: result => {
        if (result?.organisations) {
          updateOrganisationsAfterPostApiCall(result.organisations);
          setSelected([]);
          messageContext.setMessage(success(`Updated ${plural(result.organisations.length, 'organisation')}`, { icon: 'settings' }));
        } else {
          if (result?.error) {
            messageContext.setMessage(error(`Failed to update organisations due to "${result?.error}"`));
          }
        }
        setBatchUpdateDialogData({ open: false });
      },
    });
  }

  async function syncOrgs(selected: Organisation[], setSelected: Function) {
    // Sync in the background with a progress banner, so the page stays usable
    messageContext.setMessage(progress(`Syncing ${plural(selected.length, 'organisation')} with Xero`));
    setSelected([]);
    try {
      const updatedEntries = await syncTenants(selected.map(row => row.id));
      updateOrganisationsAfterPostApiCall(updatedEntries);
      messageContext.setMessage(
        syncResultMessage(
          selected.map(row => ({ id: row.id, name: row.name })),
          updatedEntries.map(entry => entry.id),
        ),
      );
    } catch (e) {
      messageContext.setMessage(error('Failed to sync with Xero. Log out and log back in if the problem persists.'));
    }
  }

  function updateOrganisationAfterPostApiCall(org: Organisation) {
    setOrganisations(current => current.map(o => (o.id === org.id ? org : o)));
  }

  function updateOrganisationsAfterPostApiCall(orgs: Organisation[]) {
    const mappedEntry = new Map<number, Organisation>(orgs.map(element => [element.id, element]));
    setOrganisations(current => current.map(entry => mappedEntry.get(entry.id) || entry));
  }

  async function updatePaygwPeriod(org: Organisation, event: any) {
    setLoading(true);
    messageContext.setMessage(empty());
    try {
      const updatedOrg = await updateOrganisations(org.id, { paygwPeriod: event.target.value });
      updateOrganisationAfterPostApiCall(updatedOrg);
    } catch (e) {
      messageContext.setMessage(error('Failed to update the PAYGW period. Please try again.'));
    }
    setLoading(false);
  }

  async function updateAutoEmailReminder(org: Organisation, checked: boolean) {
    messageContext.setMessage(empty());
    try {
      const updatedOrg = await updateOrganisations(org.id, { autoReminderBasDue: checked });
      updateOrganisationAfterPostApiCall(updatedOrg);
    } catch (e) {
      messageContext.setMessage(error('Failed to update auto reminder email setting.'));
    }
  }

  async function updateCustomFieldEmail(org: Organisation, value: string) {
    messageContext.setMessage(empty());
    try {
      const updatedOrg = await updateOrganisations(org.id, { customFieldEmail: value });
      updateOrganisationAfterPostApiCall(updatedOrg);
    } catch (e) {
      messageContext.setMessage(error('Failed to update the email custom field.'));
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        loadOrganisations();
      } catch (error) {
        console.error('There was an error', error);
      }
    };
    fetchData();
  }, []);

  const columns = withReadableFilters(
    [
      {
        name: 'name',
        label: 'Legal Name',
        options: {
          filter: true,
          sort: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = organisations[dataIndex];
            return (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: { md: '250px' } }}>
                  <div>
                    {row.name + ' '}
                    <XeroLink shortCode={row.shortCode} />
                    {row.abn && <SubText>ABN: {row.abn}</SubText>}
                  </div>
                </Box>
              </>
            );
          },
        },
      },
      {
        name: 'manager',
        label: 'Client Manager',
        options: {
          filter: true,
          filterType: 'multiselect',
          filterList: setupFilters.manager,
          sort: true,
          ...customFilterWithPrefix('Manager'),
          filterOptions: {
            names: [...new Set(organisations.map(org => (org.manager ? getFullName(org.manager) : NONE_LABEL)))].sort(),
            logic: (manager: PublicUser | null, filters: string[]) => {
              if (manager == null) {
                return !filters.includes(NONE_LABEL);
              }
              if (filters.length) return !filters.includes(getFullName(manager));
              return false;
            },
          },
          customBodyRenderLite: (dataIndex: number) => {
            const row = organisations[dataIndex];
            const openManagerDialog = async () => {
              try {
                setSelectDialogData({
                  open: true,
                  title: 'Change client manager',
                  subtitle: row.name,
                  label: 'Client manager',
                  noneLabel: 'No manager',
                  emptyState: 'No users in your firm yet.',
                  selectFunction: async (userId: number) => {
                    try {
                      messageContext.setMessage(empty());
                      const updatedOrg = await updateOrganisationManager(row.id, userId);
                      setOrganisations(current => current.map(o => (o.id === updatedOrg.id ? updatedOrg : o)));
                      messageContext.setMessage(success('Organisation manager updated', { icon: 'person' }));
                    } catch (e) {
                      messageContext.setMessage(
                        error(
                          <span>
                            Failed to update organisation manager, please try again or <SupportLink />
                          </span>,
                        ),
                      );
                    }
                  },
                  initialValue: row.manager?.id?.toString(),
                  loadData: async () => {
                    const users = user?.firm?.id ? await getFirmUsers(user?.firm?.id) : [];
                    return users.map((user): KeyValue => {
                      return {
                        key: user.id.toString(),
                        value: getFullName(user),
                        description: user.email,
                      };
                    });
                  },
                });
              } catch (exception) {
                messageContext.setMessage(error("Couldn't load users for that organisation"));
              }
            };
            return (
              <div className={styles.centered}>
                {row.manager ? (
                  <div className={styles.centered}>
                    {row.manager.givenName} {row.manager.familyName}
                    <Tooltip title="Change client manager">
                      <IconButton size="small" aria-label={`Change client manager for ${row.name}`} onClick={openManagerDialog}>
                        <EditIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                ) : (
                  <Button size="small" variant="outlined" color="warning" onClick={openManagerDialog} sx={{ whiteSpace: 'nowrap' }}>
                    Set manager
                  </Button>
                )}
              </div>
            );
          },
        },
      },
      {
        name: 'contact',
        label: 'Contact',
        options: {
          filter: true,
          filterType: 'multiselect',
          filterList: setupFilters.contact,
          ...customFilterWithPrefix('Contact'),
          filterOptions: {
            names: [...new Set(organisations.map(org => (org.contact ? getFullContactName(org.contact) : NONE_LABEL)))].sort(),
            logic: (contact: OrganisationContact | null, filters: string[]) => {
              if (contact == null) {
                return !filters.includes(NONE_LABEL);
              }
              if (filters.length) return !filters.includes(getFullContactName(contact));
              return false;
            },
          },
          sort: false,
          customBodyRenderLite(dataIndex: number) {
            const row = organisations[dataIndex];
            const openContactDialog = async () => {
              try {
                setSelectDialogData({
                  open: true,
                  title: 'Change contact',
                  subtitle: row.name,
                  label: 'Contact',
                  noneLabel: 'No contact',
                  emptyState: 'No contacts with an email address were found in Xero for this organisation.',
                  selectFunction: async (email: string) => {
                    try {
                      messageContext.setMessage(empty());
                      const updatedOrg = await updateOrganisationContact(row.id, email);
                      setOrganisations(current => current.map(o => (o.id === updatedOrg.id ? updatedOrg : o)));
                      messageContext.setMessage(success('Organisation contact updated', { icon: 'person' }));
                    } catch (e) {
                      messageContext.setMessage(
                        error(
                          <span>
                            Failed to update organisation contact, please try again or <SupportLink />
                          </span>,
                        ),
                      );
                    }
                  },
                  initialValue: row.contact?.email || '',
                  loadData: async () => {
                    const contacts = await getContacts(row.id);
                    return contacts.map((contact): KeyValue => {
                      return {
                        key: contact.email,
                        value: contact.firstName + ' ' + contact.lastName,
                        description: contact.email,
                      };
                    });
                  },
                });
              } catch (exception) {
                messageContext.setMessage(error("Couldn't load contacts for that organisation"));
              }
            };

            return (
              <div className={styles.centered}>
                {row.contact ? (
                  <div className={styles.centered}>
                    <div>
                      <div className={styles.centered}>{getFullContactName(row.contact)}</div>
                      <SubText>{row.contact.email}</SubText>
                    </div>
                    <Tooltip title="Change contact">
                      <IconButton size="small" aria-label={`Change contact for ${row.name}`} onClick={openContactDialog}>
                        <EditIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                ) : (
                  <Button size="small" variant="outlined" color="warning" onClick={openContactDialog} sx={{ whiteSpace: 'nowrap' }}>
                    Set contact
                  </Button>
                )}
              </div>
            );
          },
        },
      },
      {
        name: 'gstPeriod',
        label: 'GST Period',
        options: {
          filter: true,
          sort: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRender(value: string) {
            return humanize(value);
          },
        },
      },
      {
        name: 'paygwPeriod',
        label: 'PAYGW Period',
        options: {
          filter: true,
          sort: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRenderLite(dataIndex: number) {
            const row = organisations[dataIndex];
            return (
              <Select
                size="small"
                sx={{ mt: '4px', mb: '4px', width: '120px' }}
                id="paygw-period-select"
                value={row.paygwPeriod}
                onChange={event => updatePaygwPeriod(row, event)}
              >
                <MenuItem value={'MONTHLY'}>{PeriodEnum.MONTHLY}</MenuItem>
                <MenuItem value={'QUARTERLY'}>{PeriodEnum.QUARTERLY}</MenuItem>
              </Select>
            );
          },
        },
      },
      // Reminder emails are on Intermediate plans and above
      hasFeature(user, 'Emails') && {
        name: 'autoReminderBasDue',
        label: 'Auto reminder email',
        options: {
          hint: 'A reminder email is sent on the 14th and 28th of the month after the period ends if there are unreconciled bank transactions',
          filter: true,
          ...booleanFilter('Auto reminder'),
          sort: true,
          display: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRenderLite(dataIndex: number) {
            const row = organisations[dataIndex];
            return <CheckBox onSubmit={checked => updateAutoEmailReminder(row, checked)} checked={row.autoReminderBasDue} />;
          },
        },
      },
      {
        name: 'customFieldEmail',
        label: 'Email custom field',
        options: {
          hint: 'Free text for this organisation, inserted in emails wherever {{organisationCustomFieldEmail}} is used',
          filter: false,
          sort: true,
          // Hidden until the customer turns it on from the "View Columns" menu
          display: getDatatableColumnConfig('organisationConfig', 'customFieldEmail')?.display || 'false',
          customBodyRenderLite(dataIndex: number) {
            const row = organisations[dataIndex];
            return (
              <AddTextField
                allowEmpty={true}
                maxLength={512}
                editMinWidth="220px"
                textEditLabel="Email custom field"
                initialValue={row.customFieldEmail || ''}
                hoverText={row.customFieldEmail || 'Set email custom field'}
                onSubmit={value => updateCustomFieldEmail(row, value)}
                addIcon={
                  row.customFieldEmail ? (
                    <span className={styles.centered} aria-label={`Edit email custom field for ${row.name}`}>
                      <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.customFieldEmail}</span>
                      <EditIcon sx={{ fontSize: '16px', ml: '4px' }} />
                    </span>
                  ) : (
                    <AddTagIcon aria-label={`Set email custom field for ${row.name}`} sx={{ color: '#007ca3', verticalAlign: 'middle' }} />
                  )
                }
              />
            );
          },
        },
      },
      {
        name: 'basPeriod',
        label: 'BAS Period',
        options: {
          filter: true,
          sort: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRender(value: string) {
            return humanize(value);
          },
        },
      },
      {
        name: 'gstBasis',
        label: 'GST Basis',
        options: {
          filter: true,
          sort: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRender(value: string) {
            return humanize(value);
          },
        },
      },
      {
        name: 'tags',
        label: 'Tags',
        options: {
          sort: false,
          filter: true,
          filterType: 'multiselect',
          filterOptions: {
            names: [NONE_LABEL, ...new Set(organisations.flatMap(org => org.tags.map(t => t.name)))],
            logic: (tags: Tag[], filters: string[]) => {
              const tagNames = tags.map(t => t.name);
              if (tagNames.length == 0) {
                return !filters.includes(NONE_LABEL);
              }
              return !filters.some((filter: string) => tagNames.includes(filter));
            },
          },
          customFilterListOptions: {
            render: (value: object) => {
              return 'Tag: ' + value;
            },
          },

          customBodyRenderLite(dataIndex: number) {
            const row = organisations[dataIndex];

            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', minWidth: '180px', maxWidth: '260px', gap: '4px', padding: '4px 0' }}>
                <AddTextField
                  addIcon={<AddTagIcon sx={{ color: '#007ca3', cursor: 'pointer', verticalAlign: 'middle' }} />}
                  maxLength={32}
                  textEditLabel="Tag Name"
                  onSubmit={async value => {
                    try {
                      const org = await tagOrganisation(row.id, value);
                      updateOrganisationAfterPostApiCall(org);
                    } catch (e) {
                      messageContext.setMessage(error(`Failed to add tag "${value}". Please try again.`));
                    }
                  }}
                />

                {row.tags?.map(tag => (
                  <Chip
                    key={tag.id}
                    size="small"
                    label={tag.name}
                    variant="outlined"
                    onDelete={async () => {
                      try {
                        const org = await untagOrganisation(row.id, tag.name);
                        updateOrganisationAfterPostApiCall(org);
                      } catch (e) {
                        messageContext.setMessage(error(`Failed to remove tag "${tag.name}". Please try again.`));
                      }
                    }}
                  ></Chip>
                ))}
              </div>
            );
          },
        },
      },
      {
        name: 'gstAccountName',
        label: 'GST Account',
        options: {
          filter: false,
          sort: false,
          display: getDatatableColumnConfig('organisationConfig', 'gstAccountName')?.display || 'false',
        },
      },
      {
        name: 'paygWitholdingPayableName',
        label: 'PAYG Account',
        options: {
          filter: false,
          sort: false,
          display: getDatatableColumnConfig('organisationConfig', 'paygWitholdingPayableName')?.display || 'false',
        },
      },
      {
        name: 'wagesPayableAccountName',
        label: 'Wages Payable Account',
        options: {
          filter: false,
          sort: false,
          display: getDatatableColumnConfig('organisationConfig', 'wagesPayableAccountName')?.display || 'false',
        },
      },
      {
        name: 'superPayableAccountName',
        label: 'Superannuation Payable Account',
        options: {
          filter: false,
          sort: false,
          display: getDatatableColumnConfig('organisationConfig', 'superPayableAccountName')?.display || 'false',
        },
      },
      {
        name: 'periodLockDate',
        label: 'User Lock Date',
        options: {
          filter: false,
          sort: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRender: (value: string) => {
            return value ? displayDate(value) : EMPTY_CELL;
          },
        },
      },
      {
        name: 'endOfYearLockDate',
        label: 'Adviser Lock Date',
        options: {
          filter: false,
          sort: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRender: (value: string) => {
            return value ? displayDate(value) : EMPTY_CELL;
          },
        },
      },
      shouldShowOpenAccess && {
        name: 'connectedToCurrentUser',
        label: 'Connected by',
        options: {
          hint: 'Connected from xero by your user directly or another user in your firm',
          filter: true,
          sort: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          display: getDatatableColumnConfig('organisationConfig', 'connectedToCurrentUser')?.display || 'false',
          customBodyRender(value: boolean) {
            return value ? 'You' : 'Firm';
          },
        },
      },
      shouldShowXpm && {
        name: 'xpmClient',
        label: 'Xpm client',
        options: {
          filter: false,
          setCellProps: () => {
            return { align: 'center' };
          },
          display: getDatatableColumnConfig('organisationConfig', 'xpmClient')?.display || 'false',
          customBodyRender(value: XpmClient) {
            return value ? <ExternalLink link={value.xpmUrl} label="Xpm" /> : '';
          },
        },
      },
      {
        name: 'action',
        label: ' ',
        options: {
          download: false,
          sort: false,
          filter: false,
          viewColumns: false,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRenderLite: (dataIndex: number) => {
            const row = organisations[dataIndex];
            return (
              <>
                {row.connectedToCurrentUser ? (
                  <Button
                    size="small"
                    color="error"
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() =>
                      setDisconnectConfirmData({
                        open: true,
                        message: `Are you sure you want to disconnect "${row.name}" from Xero?`,
                        disconnectFunction: () => disconnectOrganisation(row.id),
                      })
                    }
                  >
                    Disconnect
                  </Button>
                ) : (
                  <SubText>
                    <i>
                      <DisplayWithSeeMoreChip prefix="Connected by " rows={row.connectedUsers.map(s => getFullName(s))} nbRowsDisplayed={1} separator={'\n'} />
                    </i>
                  </SubText>
                )}
              </>
            );
          },
        },
      },
    ].filter(Boolean) as MUIDataTableColumnDef[],
  );

  const tableBodyHeight = useTableBodyHeight('calc(100vh - 210px)');

  const options: MUIDataTableOptions = {
    filterType: 'dropdown',
    enableNestedDataAccess: '.',
    rowsPerPageOptions: [25, 50, 100],
    rowsPerPage: getDatatableConfig('organisationConfig', 'rowsPerPage') || 50,
    print: false,
    storageKey: 'organisationConfig',
    textLabels: {
      body: {
        noMatch: (
          <NoMatches
            message="No organisations match"
            onClear={setupFilters.manager.length || setupFilters.contact.length ? () => setSetupFilters({ manager: [], contact: [] }) : undefined}
            clearLabel="Show all organisations"
          />
        ),
      },
    },
    tableBodyHeight,
    fixedSelectColumn: true,
    onFilterChange: (_, filterList) =>
      setSetupFilters({ manager: getColumnFilter(columns, filterList, 'manager'), contact: getColumnFilter(columns, filterList, 'contact') }),
    fixedHeader: true,
    searchPlaceholder: 'Type any text to search in the organisations table',
    setTableProps: () => {
      return {
        // material ui v5 only
        size: 'small',
        //border: '1'
      };
    },
    customToolbarSelect: function (
      selectedRows: { data: Array<{ index: number; dataIndex: number }>; lookup: { [key: number]: boolean } },
      _: DisplayData,
      setSelectedRows: (rows: number[]) => void,
    ) {
      if (selectedRows.data.length > 0) {
        const selectedData = selectedRows.data.map(row => organisations[row.dataIndex]);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
            <Button size="small" variant="contained" startIcon={<BatchIcon />} onClick={() => openBatchUpdatePopup(selectedData, setSelectedRows)}>
              Update settings
            </Button>
            <Button size="small" variant="outlined" startIcon={<SyncIcon />} onClick={() => syncOrgs(selectedData, setSelectedRows)}>
              Sync with Xero
            </Button>
          </Box>
        );
      }
    },
  };

  return (
    <ThemeProvider theme={getMuiTheme()}>
      <OrgSyncStatusDisplay syncReadyCallback={onSyncReady} />

      {loading ? (
        <Loading />
      ) : organisations.length === 0 ? (
        <div style={{ padding: '10px 16px' }}>
          <PageHeader title="Organisations" description="Connect your clients' Xero organisations to start tracking their BAS." />
          <NoOrganisation alignItems={'left'} />
        </div>
      ) : (
        <div>
          <ConfirmDialog
            title="Disconnect organisation?"
            open={disconnectConfirmData.open}
            onConfirm={() => disconnectConfirmData.disconnectFunction && disconnectConfirmData.disconnectFunction()}
            setOpen={(open: boolean) => setDisconnectConfirmData({ ...disconnectConfirmData, open })}
            cancelText="Cancel"
            confirmText="Disconnect"
          >
            {disconnectConfirmData.message}
          </ConfirmDialog>

          <DialogSelect
            setOpen={(open: boolean) => setSelectDialogData({ ...selectDialogData, open })}
            open={selectDialogData.open}
            loadData={selectDialogData.loadData}
            initialValue={selectDialogData.initialValue}
            onSelect={selectDialogData.selectFunction}
            title={selectDialogData.title || ''}
            subtitle={selectDialogData.subtitle}
            label={selectDialogData.label}
            noneLabel={selectDialogData.noneLabel}
            emptyTitle={selectDialogData.emptyState || ''}
          />

          <BatchUpdateOrganisationDialog {...batchUpdateDialogData}></BatchUpdateOrganisationDialog>

          <Box sx={{ px: 1, pt: 1 }}>
            <PageHeader
              title="Organisations"
              description={
                <>
                  {organisations.length} connected
                  {user?.firm?.subscription && (
                    <>
                      {' · '}
                      {user.firm.connectionCount} of {user.firm.subscription.connectionLimit} Xero connections used on your plan
                    </>
                  )}
                </>
              }
              actions={
                <>
                  {SETUP_FILTER_COLUMNS.map(column => {
                    const missing = countMissingSetup(organisations)[column];
                    if (missing === 0) return null;
                    const active = setupFilters[column].includes(NONE_LABEL);
                    return (
                      <Tooltip
                        key={column}
                        title={active ? 'Show all organisations' : `Show organisations without a ${column === 'manager' ? 'client manager' : 'contact'}`}
                      >
                        <Chip
                          color="warning"
                          variant={active ? 'filled' : 'outlined'}
                          aria-pressed={active}
                          label={`${missing} without ${column === 'manager' ? 'a client manager' : 'a contact'}`}
                          onClick={() => setSetupFilters(current => ({ ...current, [column]: active ? [] : [NONE_LABEL] }))}
                        />
                      </Tooltip>
                    );
                  })}
                  <Button variant="contained" startIcon={<AddIcon />} href={AppConfig.oauthConnectionsPath}>
                    Connect Xero organisations
                  </Button>
                </>
              }
            />
          </Box>

          <MUIDataTable title="" data={organisations || []} columns={columns} options={options} />
        </div>
      )}
    </ThemeProvider>
  );
}
